/**
 * Shell resolver — AppShell sidebar vs. raw layout per route. Owns the
 * next-turn mutation + TurnSummary modal orchestration.
 */

import { useEffect, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AppShell, LanguageSwitcher, NextTurnButton } from "@/components/layout";
import { TurnSummaryModal } from "@/components/game/TurnSummaryModal";
import { TutorialCoach } from "@/components/TutorialCoach";
import { usePlayerStore } from "@/stores/usePlayerStore";
import { usePlayer } from "@/hooks/usePlayers";
import { usePortfolio } from "@/hooks/usePortfolio";
import { useNextTurn } from "@/hooks/useNextTurn";
import { useScenarios } from "@/hooks/useScenarios";
import { useToast } from "@/stores/useUIStore";
import { PUBLIC_ROUTE_PATHS } from "./AppRoutes";
import type { TurnResultOut } from "@/services/types";

export function Shell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const activePlayerId = usePlayerStore((s) => s.activePlayerId);
  const { data: player } = usePlayer(activePlayerId ?? undefined);
  const { data: portfolio } = usePortfolio(activePlayerId ?? undefined);
  const { data: scenarios } = useScenarios();
  const nextTurn = useNextTurn(activePlayerId ?? 0);
  const navigate = useNavigate();
  const toast = useToast();

  // Show the scenario's own time budget in the sidebar. Open-ended
  // scenarios (time_limit_months === null) get a visible infinity marker
  // instead of the misleading global 300-month cap.
  const scenario = scenarios?.find((s) => s.id === player?.scenario_id);
  const scenarioLimit = scenario?.time_limit_months ?? null;

  // Pre-bankruptcy warning — backend will force BANKRUPT when cash
  // drops below a threshold (currently −500 000 Kč). Flag the player
  // once they go negative so they can react before it's terminal.
  const showCashWarning = player ? player.cash < 0 && player.status === "active" : false;

  const [summaryOpen, setSummaryOpen] = useState(false);
  const [lastResult, setLastResult] = useState<TurnResultOut | null>(null);

  // Any non-"active" status is a terminal state — the API will 409 any
  // next-turn request, so block the UI preemptively and show a reason.
  const isGameOver = player ? player.status !== "active" : false;
  const gameOverLabels: Record<string, string> = {
    bankrupt: "Bankrot",
    victory: "Výhra",
    time_expired: "Čas vypršel",
    abandoned: "Hra opuštěna",
  };
  const gameOverLabel = player
    ? gameOverLabels[player.status] ?? player.status
    : "";

  // Keyboard shortcut: "N" advances the turn when game is active.
  // Skipped inside inputs/textareas/contenteditables so typing never
  // accidentally fires the most destructive action.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "n" && e.key !== "N") return;
      const t = e.target as HTMLElement | null;
      if (!t) return;
      const tag = t.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        t.isContentEditable
      ) {
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      e.preventDefault();
      void handleNextTurn();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePlayerId, isGameOver, nextTurn.isPending]);

  // Public / onboarding pages — render raw, no sidebar.
  if (
    pathname === "/landing" ||
    PUBLIC_ROUTE_PATHS.some((p) => pathname === p) ||
    pathname.startsWith("/onboarding")
  ) {
    return <>{children}</>;
  }

  async function handleNextTurn() {
    if (!activePlayerId) {
      toast.warning("Žádná aktivní hra");
      return;
    }
    if (isGameOver) {
      toast.warning(`Hra skončila (${gameOverLabel}) — založ nový scénář.`);
      return;
    }
    // Guard against rapid clicks that fire before `nextTurn.isPending`
    // propagates to the button's `disabled` prop via a re-render.
    if (nextTurn.isPending) {
      return;
    }
    try {
      const result = await nextTurn.mutateAsync();
      setLastResult(result);
      setSummaryOpen(true);
      if (result.bankrupted) {
        toast.error("Bankrot — hra skončila");
      } else if (result.scenario_completed) {
        toast.success(
          result.scenario_stars > 0
            ? `Scénář splněn · ${result.scenario_stars} ★`
            : "Čas vypršel — scénář nesplněn",
        );
      }
    } catch (err) {
      toast.error("Nepodařilo se spustit tah", String(err));
    }
  }

  return (
    <>
      <AppShell
        sidebarFooter={
          <div className="space-y-3">
            {showCashWarning && (
              <div className="rounded-lg border border-loss/60 bg-loss/10 p-2.5 text-[11px] leading-tight">
                <div className="font-semibold text-loss">⚠ Mínus na účtu</div>
                <div className="mt-0.5 text-ink-muted">
                  Při dalším propadu hrozí bankrot (limit −500 000 Kč). Zvaž
                  prodej nebo snížení nákladů.
                </div>
              </div>
            )}
            <NextTurnButton
              month={player?.month ?? 0}
              totalMonths={scenarioLimit ?? undefined}
              cash={player?.cash ?? 0}
              loading={nextTurn.isPending}
              onClick={handleNextTurn}
              disabled={isGameOver}
              disabledLabel={isGameOver ? gameOverLabel : undefined}
            />
            <div className="flex justify-end">
              <LanguageSwitcher />
            </div>
          </div>
        }
      >
        {children}
      </AppShell>
      <TurnSummaryModal
        open={summaryOpen}
        onOpenChange={setSummaryOpen}
        result={lastResult}
      />
      {/* Tutorial coach — only for Beginner scenario, only while no
          properties owned. Hidden after dismiss (per-player in LocalStorage). */}
      {player && player.scenario_id === "beginner" && activePlayerId && (
        <TutorialCoach
          playerId={activePlayerId}
          month={player.month}
          propertiesOwned={portfolio?.length ?? 0}
          onNavigate={(path) => navigate(path)}
        />
      )}
    </>
  );
}
